import request from '../request'

// MF-554 — Boards (Chart Library + Board bento). The viewer only reads a published Board and asks
// each tile for its data; the backend decides the data scope from the session (a student only ever
// gets their own numbers, a parent only the child they picked).

/** A published Board (layout: grid + tiles + filters). 404 when it doesn't exist or isn't yours. */
export const getBoardView = (code) =>
  request.get(`/boards/view/${encodeURIComponent(code)}`).then((res) => res?.data ?? null)

/**
 * Data for one tile: { columns, rows, encoding, meta } — meta carries how to draw it.
 * @param {string} code - board code
 * @param {string} tileId
 * @param {{ params?: Object, studentId?: string }} body - studentId: the child a parent is viewing
 */
export const getBoardTileData = (code, tileId, body = {}) =>
  request
    .post(`/boards/view/${encodeURIComponent(code)}/tiles/${encodeURIComponent(tileId)}/data`, body)
    .then((res) => res?.data ?? null)

// ── Board Builder (BoardsBuilder) and Chart Library (BoardsLibrary / builders) ───────────────────

/** Library as this school sees it: { items (+compatible, reason), providers }. */
export const getLibrary = () => request.get('/boards/library').then((res) => res?.data ?? null)

/** Live preview of an item with the whole school's data, or one student's (staff). */
export const previewLibraryItem = (itemCode, params = {}, studentCode = null) =>
  request
    .post(`/boards/library/${encodeURIComponent(itemCode)}/preview`, {
      params,
      ...(studentCode ? { studentCode } : {})
    })
    .then((res) => res?.data ?? null)

export const getBoards = (params = {}) =>
  request.get('/boards/manage', { params }).then((res) => res?.data ?? null)
export const getBoard = (id) => request.get(`/boards/manage/${id}`).then((res) => res?.data ?? null)
export const createBoard = (data) => request.post('/boards/manage', data).then((res) => res?.data ?? null)
export const updateBoard = (id, data) =>
  request.put(`/boards/manage/${id}`, data).then((res) => res?.data ?? null)
export const deleteBoard = (id) => request.delete(`/boards/manage/${id}`).then((res) => res?.data ?? null)
export const publishBoard = (id) =>
  request.post(`/boards/manage/${id}/publish`).then((res) => res?.data ?? null)
export const unpublishBoard = (id) =>
  request.post(`/boards/manage/${id}/unpublish`).then((res) => res?.data ?? null)

/** Data for every tile of a Board in one request: { tiles: { [tileId]: { data } | { error } } }. */
export const getBoardData = (code, body = {}) =>
  request.post(`/boards/view/${encodeURIComponent(code)}/data`, body).then((res) => res?.data ?? null)

/** Several Library previews at once: { "<code>|<params JSON>": { data } | { error } }. */
export const previewLibraryItems = (items = [], studentCode = null) =>
  request
    .post('/boards/library/preview', { items, ...(studentCode ? { studentCode } : {}) })
    .then((res) => res?.data ?? null)

/**
 * Students of the school's roster (the one analytics reads), for the staff picker of a Board.
 * area: 'library' (Board Builder) | 'view' (opening a published Board). → [{ code, name, year_level }]
 */
export const searchBoardStudents = (q, area = 'library') =>
  request.get(`/boards/${area}/students/search`, { params: { q } }).then((res) => res?.data ?? [])

/** MF-566 — a draft copy of a Board. */
export const cloneBoard = (id) => request.post(`/boards/manage/${id}/clone`).then((res) => res?.data ?? null)
/** MF-566 — the Board as a portable file (no data, no ids of this school). */
export const exportBoard = (id) => request.get(`/boards/manage/${id}/export`).then((res) => res?.data ?? null)
/** MF-566 — a file exported from this or another school → a draft here: { board, warnings }. */
export const importBoard = (payload) =>
  request.post('/boards/manage/import', payload).then((res) => res?.data ?? null)

// MF-577 — which Library items the school offers to builders (curators only)
export const saveLibraryCuration = (allowed) =>
  request.put('/boards/library/curation', { allowed }).then((res) => res?.data ?? null)

// MF-568 — the school's own PAT settings (norms, stanines, thresholds, teaching notes): curators only
export const getLibrarySettings = () =>
  request.get('/boards/library/settings').then((res) => res?.data ?? { pat: {} })
export const saveLibrarySettings = (pat) =>
  request.put('/boards/library/settings', { pat }).then((res) => res?.data ?? null)

// MF-575 — the values an option of a Library item can take, from the school's own data
export const getParamValues = (itemCode, key) =>
  request
    .get(`/boards/library/${encodeURIComponent(itemCode)}/params/${encodeURIComponent(key)}/values`)
    .then((res) => res?.data ?? [])
